'use strict';

(function (Cropper) {
	function ImageCrop($q, $element, $scope, $window, $timeout) {
		const $ctrl = this;
		let cropper;
		$ctrl.zoomLevel = { newLevel: 0 };
		$ctrl.minMaxSame = false;
		$ctrl.originalWidth = -1;
		$ctrl.originalHeight = -1;
		$ctrl.disableCropping = true;

		const resetDirective = () => {
			$ctrl.imageLoaded = false;
			$ctrl.disableZoom = false;
		};

		function debounce(callback, wait, immediate = false) {
			let timeout = null;

			return function () {
				const callNow = immediate && !timeout;
				const next = () => callback.apply(this, arguments);

				if (timeout != null) {
					$timeout.cancel(timeout);
				}

				timeout = $timeout(next, wait);

				if (callNow) {
					next();
				}
			};
		}

		angular.element($window).bind(
			'resize',
			debounce(() => {
				cropper.reset();
				$ctrl.zoomTo();
			}, 200)
		);

		let imageLoadListener = false;

		const getCroppingSize = () => {
			const cropData = cropper.getData();
			const width = cropData.width;
			const height = cropData.height;
			const cropToSize = { width: $ctrl.options.maxWidth, height: $ctrl.options.maxHeight };

			if (width < $ctrl.options.maxWidth && height < $ctrl.options.maxHeight) {
				cropToSize.width = width > $ctrl.options.minWidth ? width : $ctrl.options.minWidth;
				cropToSize.height = height > $ctrl.options.minHeight ? height : $ctrl.options.minHeight;
			}

			return cropToSize;
		};

		function getCroppedBlob() {
			const deferred = $q.defer();
			const cropToSize = getCroppingSize();

			if ($ctrl.fillColor) {
				cropToSize.fillColor = $ctrl.fillColor;
			}

			cropper.getCroppedCanvas(cropToSize).toBlob(function (blob) {
				deferred.resolve({
					blob: blob,
					metaData: cropToSize,
					disableCropping: $ctrl.disableCropping
				});
			}, $ctrl.file.contentType);

			return deferred.promise;
		}

		function createPreview(tryCount) {
			if (!$ctrl.options.previews) {
				return;
			}
			const img = document.querySelector('.preview-img');
			// Because: https://sentry.io/organizations/pinmeto/issues/1761932937/?project=1292828
			if (img == null) {
				tryCount = tryCount || 0;
				if (tryCount < 10) {
					$timeout(() => createPreview(tryCount + 1), 10);
				}
				return;
			}

			const selectedPreviewImage = $ctrl.options.previews[$ctrl.options.selectedPreview];
			const previewContainer = document.querySelector('.preview');

			if (previewContainer != null) {
				previewContainer.style.width = selectedPreviewImage.width;
				previewContainer.style.height = selectedPreviewImage.height;
				previewContainer.style.overflow = 'hidden';
				previewContainer.style.margin = 'auto';
			}

			return getCroppedBlob().then(({ blob }) => {
				img.src = URL.createObjectURL(blob);
				img.style.objectFit = $ctrl.disableCropping
					? 'cover'
					: selectedPreviewImage.objectFit($ctrl.options.selectedOptimize);
				img.style.width = selectedPreviewImage.width;
				img.style.height = selectedPreviewImage.height;

				const onPreviewLoaded = () => {
					URL.revokeObjectURL(img.src);
				};

				if (imageLoadListener) {
					img.removeEventListener('load', onPreviewLoaded);
					imageLoadListener = false;
				}

				img.addEventListener('load', onPreviewLoaded);
				imageLoadListener = true;
			});
		}

		$ctrl.switchPreview = preview => {
			$ctrl.options.selectedPreview = preview.name;
			cropper.crop();
		};

		let toggleCroppingDebounce;

		function initCropImage(file) {
			$timeout.cancel(toggleCroppingDebounce);
			$ctrl.zoomLevel.newLevel = 0;
			resetDirective();
			if (cropper) {
				cropper.destroy();
			}

			const delay = 300;
			const image = $element[0].querySelector('.pmtImageToCrop');
			image.src = file.url; // This is to be sure the source is correct at this loading moment no angular magic.

			cropper = new Cropper(image, {
				viewMode: 1,
				dragMode: 'move',
				aspectRatio: $ctrl.disableCropping ? null : $ctrl.options.aspectRatio,
				autoCropArea: 1,
				minCropBoxWidth: $ctrl.options.minWidth,
				minCropBoxHeight: $ctrl.options.minHeight,
				modal: true,
				guides: false,
				center: true,
				highlight: false,
				cropBoxMovable: false,
				cropBoxResizable: false,
				toggleDragModeOnDblclick: false,
				scalable: false,
				zoomOnTouch: false,
				zoomOnWheel: false,
				responsive: true,
				restore: true,
				ready: function () {
					$ctrl.zoomTo();
					$ctrl.imageLoaded = true;
					$ctrl.originalWidth = file.metaData.width;
					$ctrl.originalHeight = file.metaData.height;

					if ($ctrl.options.previews) {
						// angular timing issues, initial cropping for preview needs to be delayed to render ¯\_(ツ)_/¯
						$timeout(() => cropper.crop(), delay);
					}

					$scope.$apply();
				},
				crop: debounce(() => {
					$timeout(() => createPreview(), 0);
				}, delay)
			});

			$ctrl.minMaxSame =
				$ctrl.options.minWidth === $ctrl.options.maxWidth &&
				$ctrl.options.minHeight === $ctrl.options.maxHeight;
		}

		$ctrl.toggleCropping = () => {
			cropper.options.aspectRatio = $ctrl.disableCropping ? null : $ctrl.options.aspectRatio;
			$timeout.cancel(toggleCroppingDebounce);
			resetDirective();
			toggleCroppingDebounce = $timeout(() => initCropImage($ctrl.file), 400);
		};

		$ctrl.zoomTo = () => {
			if (cropper.canvasData == null) {
				return;
			}

			const zoom = parseFloat($ctrl.zoomLevel.newLevel);

			const minX = cropper.canvasData.minWidth / cropper.canvasData.naturalWidth;
			const minY = cropper.canvasData.minHeight / cropper.canvasData.naturalHeight;
			const min = Math.min(minY, minX);

			const maxX = cropper.canvasData.minWidth / $ctrl.options.minWidth;
			const maxY = cropper.canvasData.minHeight / $ctrl.options.minHeight;
			const max = Math.min(maxY, maxX);

			const zoomFactor = min + (max - min) * zoom;
			cropper.zoomTo(zoomFactor);
		};

		$ctrl.doCrop({ cb: getCroppedBlob });

		$ctrl.$onChanges = function (changesObj) {
			if (changesObj.file && changesObj.file.currentValue) {
				let file = changesObj.file.currentValue;
				$ctrl.imageHasCorrectAspect =
					$ctrl.options.aspectRatio.toFixed(1) ===
					(file.metaData.width / file.metaData.height).toFixed(1);
				$ctrl.disableCropping = $ctrl.imageHasCorrectAspect;
				initCropImage(file);
			}
		};

		$ctrl.$onDestroy = function () {
			resetDirective();
		};
	}

	ImageCrop.$inject = ['$q', '$element', '$scope', '$window', '$timeout'];

	angular.module('pmtPlacesApp.directives').component('pmtImageCrop', {
		templateUrl: 'components/fileManager/images/pmtImageCrop.html',
		controller: ImageCrop,
		bindings: {
			done: '&',
			file: '<',
			doCrop: '&',
			fillColor: '<',
			options: '<'
		}
	});
})(window.Cropper);
